import { registerLocaleData } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpModule } from '@angular/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import * as Raven from 'raven-js';

import { AppRoutingModule } from './app-routing.module';

// PrimeNG
import {
  CalendarModule,
  CheckboxModule,
  DropdownModule,
  GrowlModule,
  MultiSelectModule,
  RadioButtonModule,
  SelectButtonModule,
  DialogModule,
} from 'primeng/primeng';

// Providers
import { AuthGuard } from './_guards/auth.guard';
import { AuthenticationService } from './_services/authentication.service';
import { CaptureAggregateService } from './_services/capture-aggregate.service';
import { CaptureImportedService } from './_services/capture-imported.service';
import { DashboardService } from './_services/dashboard.service';
import { DebarkationImportedService } from './_services/debarkation-imported.service';
import { DiscardAggregateService } from './_services/discard-aggregate.service';
import { DiscardImportedService } from './_services/discard-imported.service';
import { EffortService } from './_services/effort.service';
import { ExpirationService } from './_services/expiration.service';
import { ExportDataService } from './_services/export-data.service';
import { FaoZoneService } from './_services/fao-zone.service';
import { GestcuotaService } from './_services/gestcuota.service';
import { GesteffortService } from './_services/gesteffort.service';
import { HeatmapCapturesService } from './_services/heatmap-captures.service';
import { ImportDataService } from './_services/import-data.service';
import { IncidenceService } from './_services/incidence.service';
import { MapQuotaService } from './_services/map-quota.service';
import { MapsDashboardService } from './_services/maps-dashboard.service';
import { NotificationSenderService } from './_services/notification-sender.service';
import { NotificationService } from './_services/notification.service';
import { NotificationsService } from './_services/notifications.service';
import { PortAggregateService } from './_services/port-aggregate.service';
import { PortService } from './_services/port.service';
import { ProfileService } from './_services/profile.service';
import { QuotaAggregateService } from './_services/quota-aggregate.service';
import { QuotaImportedService } from './_services/quota-imported.service';
import { QuotaService } from './_services/quota.service';
import { QuotaShipService } from './_services/quotaship.service';
import { SalesPurchasesService } from './_services/sales-purchases.service';
import { SaleOfferService } from './_services/commercialization.service';
import { InfoCommercializationAssociationService } from './_services/commercialization.service';
import { ShipService } from './_services/ship.service';
import { ShipownerService } from './_services/shipowner.service';
import { SpecieService } from './_services/specie.service';
import { StockService } from './_services/stock.service';
import { TideService } from './_services/tide.service';
import { ZoneService } from './_services/zone.service';
import { HttpErrorHandler } from './shared/http-error-handler';

// Internationalization
import localees from '@angular/common/locales/es';

registerLocaleData(localees);

// Pipes
import { ForcePositiveSign } from './_pipes/forcepositivesign.pipe';

// Components
import { AppComponent } from './app.component';
import { CaptureFilterComponent } from './captures/capture-filter/capture-filter.component';
import { CapturesListComponent } from './captures/captures-list/captures-list.component';
import { AppMenuComponent } from './common/app-menu/app-menu.component';
import { ConfirmationToggleComponent } from './common/confirmation-toggle/confirmation-toggle.component';
import { ErrorPageComponent } from './common/error-page/error-page.component';
import { InlineInputComponent } from './common/inline-input/inline-input.component';
import { NotificationsComponent } from './common/notifications/notifications.component';
import { OrderingComponent } from './common/ordering/ordering.component';
import { PaginationComponent } from './common/pagination/pagination.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { DiscardsFilterComponent } from './discards/discards-filter/discards-filter.component';
import { DiscardsListComponent } from './discards/discards-list/discards-list.component';
import { ExpirationListComponent } from './expirations/expiration-list/expiration-list.component';
import { IncidenceFilterComponent } from './incidences/incidence-filter/incidence-filter.component';
import { IncidenceListComponent } from './incidences/incidence-list/incidence-list.component';
import { LoginComponent } from './login/login.component';
import { NotificationFilterComponent } from './notifications/notification-filter/notification-filter.component';
import { NotificationFormComponent } from './notifications/notification-form/notification-form.component';
import { NotificationListComponent } from './notifications/notification-list/notification-list.component';
import { PortFilterComponent } from './ports/port-filter/port-filter.component';
import { PortListComponent } from './ports/port-list/port-list.component';
import { QuotaAggregateFilterComponent } from './quotas/quota-aggregate-filter/quota-aggregate-filter.component';
import { QuotaDetailComponent } from './quotas/quota-detail/quota-detail.component';
import { QuotaFilterComponent } from './quotas/quota-filter/quota-filter.component';
import { QuotaListAggregateComponent } from './quotas/quota-list-aggregate/quota-list-aggregate.component';
import { QuotaListComponent } from './quotas/quota-list/quota-list.component';
import { QuotaNewFormComponent } from './quotas/quota-new-form/quota-new-form.component';
import { ShipFormComponent } from './ships/ship-form/ship-form.component';
import { ShipListComponent } from './ships/ship-list/ship-list.component';
import { TideDetailComponent } from './tides/tide-detail/tide-detail.component';
import { TideFilterComponent } from './tides/tide-filter/tide-filter.component';
import { TideListComponent } from './tides/tide-list/tide-list.component';

import { environment } from '../environments/environment';
import { FooterComponent } from './common/footer/footer.component';
import { ExportFilterComponent } from './export/export-filter/export-filter.component';
import { ExportPageComponent } from './export/export-page/export-page.component';
import { ExportViewComponent } from './export/export-view/export-view.component';
import { ImportPageComponent } from './import/import-page/import-page.component';
import { ImportViewComponent } from './import/import-view/import-view.component';
import { PasswordResetConfirmComponent } from './password/password-reset-confirm/password-reset-confirm.component';
import { PasswordResetRequestComponent } from './password/password-reset-request/password-reset-request.component';
import { ProfileActivateComponent } from './profile/profile-activate/profile-activate.component';
import { ProfileChangePasswordFormComponent } from './profile/profile-change-password-form/profile-change-password-form.component';
import { ProfileDetailComponent } from './profile/profile-detail/profile-detail.component';
import { QuotaTransferFormComponent } from './quotas/quota-batchs/components/quota-transfer-form/quota-transfer-form.component';
import { QuotaTransferTargetFormComponent } from './quotas/quota-batchs/components/quota-transfer-target-form/quota-transfer-target-form.component';
import { QuotaMovementsBatchListComponent } from './quotas/quota-batchs/quota-movements-batch-list/quota-movements-batch-list.component';
import { QuotaMovementsBatchComponent } from './quotas/quota-batchs/quota-movements-batch/quota-movements-batch.component';

import { HeatmapCapturesComponent } from './maps/heatmaps_captures/heatmap_captures.component';
import { HeatmapDiscardsComponent } from './maps/heatmaps_discards/heatmap_discards.component';
import { MapsDashboardComponent } from './maps/maps_dashboard/maps_dashboard.component';
import { MapQuotaComponent } from './maps/maps_quotas/maps_quotas.component';

import { EntityService } from './_services/entity.service';
import { EffortFormComponent } from './efforts/effort-form/effort-form.component';
import { EffortListComponent } from './efforts/effort-list/effort-list.component';
import { EffortsFilterComponent } from './efforts/efforts-filter/efforts-filter.component';
import { ExpirationInputComponent } from './expirations/expiration-item/expiration-input/expiration-input.component';
import { ExpirationItemComponent } from './expirations/expiration-item/expiration-item.component';
import { SalesFilterComponent } from './sales/sales-filter/sales-filter.component';
import { SalesListComponent } from './sales/sales-list/sales-list.component';
import { SaleOffersListComponent } from './sale-offers/sale-offers-list/sale-offers-list.component';
import { SaleOfferFormComponent } from './sale-offers/sale-offer-form/sale-offer-form.component';
import { SaleOffersFilterComponent } from './sale-offers/sale-offers-filter/sale-offers-filter.component';
import { InfoCommercializationAssociationFormComponent } from './sale-offers/info-commercialization-association-form/info-commercialization-association-form.component';

import { DocumentListComponent } from './documents/document-list/document-list.component';
import { DocumentFormComponent } from './documents/document-form/document-form.component';
import { DocumentFilterComponent } from './documents/document-filter/document-filter.component';
import { DocumentService } from './_services/document.service';

const sentryDSN = environment.sentryDSN;
Raven.config(sentryDSN).install();

export class RavenErrorHandler implements ErrorHandler {
  handleError(err: any): void {
    if (sentryDSN === null) {
      console.log(err);
    } else {
      Raven.captureException(err.originalError);
    }
  }
}

@NgModule({
  declarations: [
    ForcePositiveSign,
    DashboardComponent,
    ShipListComponent,
    ShipFormComponent,
    AppMenuComponent,
    LoginComponent,
    AppComponent,
    TideListComponent,
    TideDetailComponent,
    IncidenceListComponent,
    PaginationComponent,
    TideFilterComponent,
    IncidenceFilterComponent,
    CapturesListComponent,
    CaptureFilterComponent,
    ConfirmationToggleComponent,
    OrderingComponent,
    ErrorPageComponent,
    DiscardsListComponent,
    DiscardsFilterComponent,
    QuotaListComponent,
    QuotaListAggregateComponent,
    QuotaFilterComponent,
    QuotaAggregateFilterComponent,
    InlineInputComponent,
    ExpirationListComponent,
    QuotaDetailComponent,
    NotificationsComponent,
    NotificationListComponent,
    NotificationFormComponent,
    NotificationFilterComponent,
    PortListComponent,
    PortFilterComponent,
    QuotaNewFormComponent,
    ProfileActivateComponent,
    ProfileDetailComponent,
    ProfileChangePasswordFormComponent,
    ExportPageComponent,
    ExportFilterComponent,
    ExportViewComponent,
    ImportPageComponent,
    ImportViewComponent,
    PasswordResetRequestComponent,
    PasswordResetConfirmComponent,
    QuotaTransferFormComponent,
    QuotaMovementsBatchListComponent,
    QuotaMovementsBatchComponent,
    QuotaTransferTargetFormComponent,
    FooterComponent,
    MapsDashboardComponent,
    MapQuotaComponent,
    HeatmapCapturesComponent,
    HeatmapDiscardsComponent,
    EffortListComponent,
    EffortFormComponent,
    EffortsFilterComponent,
    SalesListComponent,
    SalesFilterComponent,
    SaleOffersListComponent,
    SaleOfferFormComponent,
    SaleOffersFilterComponent,
    InfoCommercializationAssociationFormComponent,
    ExpirationItemComponent,
    ExpirationInputComponent,
    DocumentFormComponent,
    DocumentListComponent,
    DocumentFilterComponent,
  ],
  imports: [
    ReactiveFormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpModule,
    AppRoutingModule,
    CalendarModule,
    DropdownModule,
    GrowlModule,
    MultiSelectModule,
    RadioButtonModule,
    CheckboxModule,
    SelectButtonModule,
    DialogModule,
  ],
  providers: [
    { provide: ErrorHandler, useClass: RavenErrorHandler },
    { provide: LOCALE_ID, useValue: 'es' },
    FormBuilder,
    AuthGuard,
    AuthenticationService,
    DashboardService,
    ShipService,
    TideService,
    IncidenceService,
    CaptureAggregateService,
    DiscardAggregateService,
    SpecieService,
    FaoZoneService,
    PortService,
    StockService,
    QuotaAggregateService,
    QuotaService,
    QuotaShipService,
    ProfileService,
    HttpErrorHandler,
    ExpirationService,
    NotificationSenderService,
    NotificationsService,
    NotificationService,
    PortService,
    PortAggregateService,
    ShipownerService,
    ExportDataService,
    ImportDataService,
    CaptureImportedService,
    DiscardImportedService,
    DebarkationImportedService,
    QuotaImportedService,
    MapsDashboardService,
    MapQuotaService,
    ZoneService,
    HeatmapCapturesService,
    EffortService,
    GestcuotaService,
    GesteffortService,
    SalesPurchasesService,
    SaleOfferService,
    InfoCommercializationAssociationService,
    EntityService,
    DocumentService,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts